//External
import React, { useEffect, useMemo, useRef, useState } from "react";
//Internal
import IconChevronDown from "../icons/icons-chevron-down";
import FilterDropdownPanel from "./FilterDropdownPanel";
import { countMatchingVehicles } from "./availabilityPool";

// Fixed price tiers shown as single-select options. The "Custom Range" row
// from the design (with its own Edit-to-open-a-slider flow) is intentionally
// not implemented yet.
const PRICE_PERIOD = "/day";

const PRICE_TIERS = [
  { label: `Under $500${PRICE_PERIOD}`, max: 500 },
  { label: `Under $1,000${PRICE_PERIOD}`, max: 1000 },
  { label: `Under $1,500${PRICE_PERIOD}`, max: 1500 },
  { label: `Under $2,000${PRICE_PERIOD}`, max: 2000 },
];

/*******************
@purpose : true when a price range matches a given tier (all tiers here are
           "no minimum, under some max")
@Parameter : {tier, priceRange}
@Author : INIC
******************/
const tierMatches = (tier, priceRange) =>
  (priceRange?.min ?? null) === null && priceRange?.max === tier.max;

const formatCurrency = (value) => `$${Number(value).toLocaleString()}`;

/*******************
@purpose : label for a price range that doesn't match any fixed tier (e.g.
           set via the Filters dialog's slider), so the trigger still
           reflects what's actually applied instead of falling back to the
           plain "Price Range" placeholder
@Parameter : {priceRange}
@Author : INIC
******************/
const formatCustomRangeLabel = (priceRange) => {
  const hasMin = priceRange?.min != null;
  const hasMax = priceRange?.max != null;
  if (hasMin && hasMax) {
    return `${formatCurrency(priceRange.min)} - ${formatCurrency(priceRange.max)}${PRICE_PERIOD}`;
  }
  if (hasMax) return `Under ${formatCurrency(priceRange.max)}${PRICE_PERIOD}`;
  if (hasMin) return `Over ${formatCurrency(priceRange.min)}${PRICE_PERIOD}`;
  return null;
};

/*******************
@Purpose : "Price Range" filter dropdown - single-select price tiers (radio
           buttons, not checkboxes), a live "Show N cars" count for the
           in-progress pick, and a Reset that empties the draft entirely
           (radios can't be unchecked by clicking them again). Same
           draft-then-apply pattern as MultiSelectFilterDropdown, just for a
           single value instead of a list.
@Parameter : {priceRange, setPriceRange, otherFilters, pool}
@Author : INIC
******************/
function PriceRangeFilterDropdown({
  priceRange,
  setPriceRange,
  otherFilters = {},
  pool,
  colorList,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftPriceRange, setDraftPriceRange] = useState(priceRange);
  const containerRef = useRef();

  /*******************
  @purpose : start each open with a fresh draft copied from the last applied
             selection, so re-opening doesn't carry over an abandoned edit
  @Author : INIC
  ******************/
  useEffect(() => {
    if (isOpen) {
      setDraftPriceRange(priceRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // "Show N cars" for the in-progress pick (combined with the other
  // already-wired filters) - recomputed instantly on every click, straight
  // off the pool.
  const previewCount = countMatchingVehicles(
    pool,
    {
      ...otherFilters,
      minPrice: draftPriceRange?.min ?? undefined,
      maxPrice: draftPriceRange?.max ?? undefined,
    },
    colorList
  );

  /*******************
  @purpose : empty the draft entirely (no tier selected)
  @Author : INIC
  ******************/
  const handleReset = () => {
    setDraftPriceRange({ min: null, max: null });
  };

  const handleApply = () => {
    setPriceRange(draftPriceRange);
    setIsOpen(false);
  };

  const appliedTier = useMemo(
    () => PRICE_TIERS.find((tier) => tierMatches(tier, priceRange)),
    [priceRange]
  );
  const customRangeLabel = !appliedTier && formatCustomRangeLabel(priceRange);
  const triggerLabel = appliedTier?.label || customRangeLabel || "Price Range";
  const hasSelection = Boolean(appliedTier) || Boolean(customRangeLabel);

  const isDraftEmpty = draftPriceRange?.min == null && draftPriceRange?.max == null;

  return (
    <div className="filter-dropdown" ref={containerRef}>
      <button
        type="button"
        className={`filter-dropdown-trigger ${hasSelection ? "has-selection" : ""} ${isOpen ? "is-open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="filter-dropdown-trigger-label">{triggerLabel}</span>
        <IconChevronDown className={`reservation-chevron ${isOpen ? "is-open" : ""}`} />
      </button>

      <FilterDropdownPanel
        isOpen={isOpen}
        anchorRef={containerRef}
        onOutsideClose={() => setIsOpen(false)}
        className="filter-dropdown-panel--multiselect"
      >
        <div className="filter-options-list">
          {PRICE_TIERS.map((tier) => {
            const checked = tierMatches(tier, draftPriceRange);
            return (
              <label key={tier.label} className="filter-checkbox-row">
                <input
                  type="radio"
                  name="price-range-tier"
                  checked={checked}
                  onChange={() => setDraftPriceRange({ min: undefined, max: tier.max })}
                />
                <span className={`filter-radio-circle ${checked ? "is-checked" : ""}`}>
                  {checked && <span className="filter-radio-dot" />}
                </span>
                <span className="filter-checkbox-label">{tier.label}</span>
              </label>
            );
          })}
        </div>

        <div className="filter-dropdown-footer">
          <button
            type="button"
            className="filter-reset-btn"
            disabled={isDraftEmpty}
            onClick={handleReset}
          >
            Reset
          </button>
          <button type="button" className="filter-apply-btn" onClick={handleApply}>
            {pool?.length
              ? `Show ${previewCount} ${previewCount === 1 ? "car" : "cars"}`
              : "Show cars"}
          </button>
        </div>
      </FilterDropdownPanel>
    </div>
  );
}

export default PriceRangeFilterDropdown;
